import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from 'react';

import { isMobile } from 'react-device-detect';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nesso/ui/primitives/tooltip';

import { TOOLTIP_TIMEOUT } from '@/shared/constants/tooltip';

type TooltipButtonProps = ComponentProps<typeof Button> & {
    tooltip?: ReactNode;
    tooltipContentProps?: ComponentProps<typeof TooltipContent>;
    tooltipProps?: ComponentProps<typeof Tooltip>;
};

export function TooltipButton({
    tooltip,
    tooltipContentProps,
    tooltipProps,
    disabled,
    className,
    onClick,
    onPointerDown,
    ...buttonProps
}: TooltipButtonProps) {
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Mobile has no hover, so a disabled button surfaces its tooltip on tap instead.
    const mobileToggle = isMobile && !!disabled;

    useEffect(() => {
        if (mobileToggle && open) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setOpen(false), TOOLTIP_TIMEOUT);
        }
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [mobileToggle, open]);

    // aria-disabled instead of the native attribute keeps the button focusable and named for
    // screen readers (so it can be the tooltip trigger directly); we block the action ourselves.
    const button = (
        <Button
            {...buttonProps}
            aria-disabled={disabled || undefined}
            className={cn('aria-disabled:opacity-50 aria-disabled:cursor-not-allowed', className)}
            onClick={(event) => {
                if (disabled) {
                    event.preventDefault();
                    if (mobileToggle) setOpen((value) => !value);
                    return;
                }
                onClick?.(event);
            }}
            onPointerDown={(event) => {
                if (mobileToggle) event.preventDefault();
                onPointerDown?.(event);
            }}
        />
    );

    if (!tooltip) {
        return button;
    }

    return (
        <Tooltip {...tooltipProps} {...(mobileToggle && { open, onOpenChange: setOpen })}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
        </Tooltip>
    );
}
